import React, { useEffect, useState } from "react";
import AVATAR_IMPORTS from "../assets/avatar/index";
import { API_BASE_URL } from "../services/apiClient";
import { getFileUrl } from "../utils/fileUrlHelper";
import CachedImage from "./CachedImage";

/**
 * AvatarImg — Merkezi avatar bileşeni
 * Hazır avatarı veya güvenli sunucu profil fotoğrafını gösterir; yoksa baş harf fallback'i kullanır.
 */
export default function AvatarImg({
  avatar,
  renk = "#6366f1",
  initials = "?",
  size = 38,
  glow = false,
  radius = "50%",
  className = "",
  style = {},
  onClick,
  onImageError,
  isOnline = false,
  uid,
  fileKey,
  ...domProps
}) {
  const r = renk || "#6366f1";
  const presetSrc = avatar ? (AVATAR_IMPORTS[avatar] || null) : null;
  const customSrc = (() => {
    if (!avatar || presetSrc || typeof window === "undefined") return null;
    try {
      // Capacitor/WebView içinde `/files/...` adresini frontend origin'ine değil,
      // merkezi yardımcı üzerinden API origin'ine bağla. Kaynak allow-list kontrolü
      // korunur; dış alan adlarından avatar yüklenmesine izin verilmez.
      const resolved = getFileUrl(String(avatar));
      const parsed = new URL(resolved, window.location.origin);
      const apiOrigin = new URL(API_BASE_URL, window.location.origin).origin;
      const allowedOrigin = parsed.origin === apiOrigin || parsed.origin === window.location.origin;
      const allowedPath = /^\/files\/(?:view|preview|stream)\//.test(parsed.pathname);
      return allowedOrigin && allowedPath ? parsed.href : null;
    } catch {
      return null;
    }
  })();
  const src = presetSrc || customSrc;
  const [imageFailed, setImageFailed] = useState(false);
  useEffect(() => { setImageFailed(false); }, [src]);
  const isCustomPhoto = Boolean(customSrc);
  const normalizedUid = String(uid || "").trim();
  const currentUid = String(window.__fpCurrentUid || "").trim();
  const globalOnline = normalizedUid && normalizedUid !== currentUid && !!window.__fpOnlineUserIds?.has?.(normalizedUid);
  const showOnlineRing = normalizedUid && normalizedUid !== currentUid ? (isOnline === true || globalOnline) : isOnline === true;
  const persistentCacheKey = isCustomPhoto && normalizedUid && fileKey
    ? `profile-avatar:${normalizedUid}:${String(fileKey)}`
    : "";

  const initialsNode = (
    <span
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%",
        color: "#fff",
        fontSize: Math.round(Number(size) * 0.37),
        fontWeight: 900,
        letterSpacing: "-0.5px",
        userSelect: "none",
      }}
    >
      {initials}
    </span>
  );

  const glowStyle = glow
    ? { boxShadow: `0 0 0 2.5px ${r}55, 0 0 14px 3px ${r}40, 0 4px 18px ${r}35` }
    : {};

  const baseStyle = {
    width: size,
    height: size,
    borderRadius: radius,
    background: `linear-gradient(135deg, ${r}, ${r}bb)`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    overflow: "visible",
    position: "relative",
    ...glowStyle,
    boxShadow: glowStyle.boxShadow,
    ...style,
  };

  return (
    <div
      className={`avatarImgWrap${glow ? " avatarGlow" : ""}${isCustomPhoto ? " avatarCustomPhoto" : ""}${className ? " " + className : ""}`}
      style={baseStyle}
      onClick={onClick}
      {...domProps}
    >
      <span
        className="avatarImgInner"
        style={{
          width: "100%",
          height: "100%",
          padding: isCustomPhoto ? Math.max(2, Math.round(Number(size) * 0.035)) : 0,
          borderRadius: "inherit",
          background: `linear-gradient(135deg, ${r}, ${r}bb)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        {src && !imageFailed ? (
          <CachedImage
            src={src}
            cacheKey={persistentCacheKey}
            fallback={initialsNode}
            alt={initials}
            onError={(event) => {
              setImageFailed(true);
              onImageError?.(event);
            }}
            className="avatarCachedImage"
            style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "inherit", display: "block" }}
          />
        ) : (
          initialsNode
        )}
      </span>
      {showOnlineRing && <span className="avatarPresenceRing" aria-hidden="true" />}
    </div>
  );
}
